import type { ITrackPlaybackSource } from '../../../../shared';

// googlevideo 4 ships declarations generated by TypeScript 5; this client is
// still on TS 4.6, so keep the small runtime boundary typed locally.
const loadSabrRuntime = () => require('googlevideo/sabr-streaming-adapter') as {
    SabrStreamingAdapter: new (options: Record<string, unknown>) => any;
    SabrUmpProcessor: new (metadata: any, cache?: any) => {
        processChunk(data: Uint8Array): Promise<{ data?: Uint8Array; done: boolean } | undefined>;
    };
};
const loadUtilsRuntime = () => require('googlevideo/utils') as {
    buildSabrFormat(format: ITrackPlaybackSource['sabr']['formats'][number]): any;
};

interface RequestMetadataManager {
    getRequestMetadata(url: string, remove?: boolean): any;
}

interface ShakaRequest {
    uris: string[];
    method: string;
    headers: Record<string, string>;
    body?: ArrayBuffer | ArrayBufferView | null;
}

interface ShakaResponse {
    uri: string;
    data: ArrayBuffer;
    headers: Record<string, string>;
    originalRequest?: ShakaRequest;
}

interface ShakaContext {
    type?: number;
    segment?: { startTime?: number; getStartTime?: () => number | null };
}

interface NetworkingEngine {
    registerRequestFilter(filter: (type: number, request: ShakaRequest, context?: ShakaContext) => Promise<void>): void;
    registerResponseFilter(filter: (type: number, response: ShakaResponse, context?: ShakaContext) => Promise<void>): void;
}

interface ShakaPlayer {
    getNetworkingEngine(): NetworkingEngine | null;
    getStats(): { estimatedBandwidth?: number };
}

interface SchemeRuntime {
    registerScheme(scheme: string, plugin: (...args: any[]) => any): void;
    unregisterScheme(scheme: string): void;
    createAbortableOperation<T>(promise: Promise<T>, onAbort: () => Promise<void>): any;
}

type RequestInterceptor = (request: any) => Promise<any> | any;
type ResponseInterceptor = (response: any) => Promise<any> | any;

export interface SabrTimingEvent {
    phase: 'request' | 'response' | 'processed' | 'delivered' | 'server';
    url: string;
    range?: string;
    hasSegment: boolean;
    bytes?: number;
    responseHash?: string;
    prefixHash?: string;
    mediaEndBytes?: number;
    coalescingStatus?: 'tail' | 'cache-hit' | 'plan-missing' | 'range-mismatch' | 'busy'
        | 'fallback-size' | 'fallback-prefix' | 'fallback-joined-size' | 'tail-joined';
    networkRange?: string;
    expectedBytes?: number;
    actualBytes?: number;
    resourceTiming?: SabrResourceTiming;
    proxyDiagnostic?: SabrProxyDiagnostic;
}

export interface SabrProxyDiagnostic {
    upstreamReadCompleteMs?: number;
    downstreamFirstWriteMs?: number;
    downstreamFinishMs?: number;
    downstreamCloseMs?: number;
    prematureClose?: boolean;
    firstBackpressureMs?: number;
    lastDrainMs?: number;
    backpressureCount: number;
}

export interface SabrResourceTiming {
    name: string;
    entryType: 'resource';
    startTime: number;
    duration: number;
    responseStart: number;
    responseEnd: number;
    encodedBodySize: number;
    serverTiming: ReadonlyArray<{ name: string; duration: number; description?: string }>;
}

type CoalescingDiagnostics = Pick<SabrTimingEvent,
    'coalescingStatus' | 'networkRange' | 'expectedBytes' | 'actualBytes'>;

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const HASH_PREFIX_BYTES = 16 * 1024;
const FIRST_MEDIA_PREFIX_BYTES = 64 * 1024;

interface ByteRange { start: number; end: number }
interface InitialRangePlan { index: ByteRange; combined: ByteRange }
interface CoalescedRequest {
    formatKey: string;
    mode: 'tail';
    logicalRange: ByteRange;
    networkRange: ByteRange;
    originalUrl: string;
    originalHeaders: Record<string, string>;
}

interface StreamingRequest {
    proxyUrl: string;
    context?: ShakaContext;
    timingHeaders: Record<string, string>;
}

let schemeSequence = 0;

const updateFnv1a = (hash: number, data: Uint8Array): number => {
    for (let index = 0; index < data.byteLength; index += 1) {
        hash = Math.imul((hash ^ data[index]) >>> 0, FNV_PRIME) >>> 0;
    }
    return hash;
};

const formatHash = (hash: number): string => hash.toString(16).padStart(8, '0');

export class ShakaSabrPlayerAdapter {
    private metadataManager?: RequestMetadataManager;
    private cache: any;
    private requestInterceptor?: RequestInterceptor;
    private responseInterceptor?: ResponseInterceptor;
    private filtersRegistered = false;
    private timingContexts = new Map<string, { range?: string; hasSegment: boolean } & CoalescingDiagnostics>();
    private localObjectUrls = new Set<string>();
    private initialRangePlans = new Map<string, InitialRangePlan>();
    private coalescedPrefixes = new Map<string, ArrayBuffer>();
    private seedUrls = new Map<string, string>();
    private seedPromises = new Map<string, Promise<boolean>>();
    private coalescedRequests = new Map<string, CoalescedRequest>();
    private seedControllers = new Set<AbortController>();
    private streamingRequests = new Map<string, StreamingRequest>();
    private streamedResponses = new Set<string>();
    private activeStreamingControllers = new Set<AbortController>();
    private readonly streamingScheme = `ytmsabr${schemeSequence++}`;
    private schemeRegistered = false;
    private hedgeRequestsRemaining = 2;

    constructor(
        private readonly player: ShakaPlayer,
        private readonly mediaElement: HTMLMediaElement,
        private readonly segmentRequestType: number,
        private readonly onTiming?: (event: SabrTimingEvent) => void,
        private readonly schemeRuntime?: SchemeRuntime
    ) {}

    initialize(_player: unknown, metadataManager: RequestMetadataManager, cache: any): void {
        this.metadataManager = metadataManager;
        this.cache = cache;
        this.registerStreamingScheme();
        if (this.filtersRegistered) return;
        const networkingEngine = this.player.getNetworkingEngine();
        if (!networkingEngine) throw new Error('Shaka networking engine is unavailable');
        networkingEngine.registerRequestFilter(this.filterRequest.bind(this));
        networkingEngine.registerResponseFilter(this.filterResponse.bind(this));
        this.filtersRegistered = true;
    }

    registerRequestInterceptor(interceptor: RequestInterceptor): void {
        this.requestInterceptor = interceptor;
    }

    registerResponseInterceptor(interceptor: ResponseInterceptor): void {
        this.responseInterceptor = interceptor;
    }

    configureInitialRanges(manifest: string, formats: ITrackPlaybackSource['sabr']['formats']): void {
        this.seedControllers.forEach(controller => controller.abort());
        this.seedControllers.clear();
        this.seedPromises.clear();
        this.coalescedPrefixes.clear();
        this.initialRangePlans.clear();
        this.seedUrls = new Map(formats.flatMap(format => format.seedUrl
            ? [[String(format.itag), format.seedUrl] as const]
            : []));
        const contentLengths = new Map(formats.map(format => [String(format.itag), Number(format.content_length)]));
        try {
            let xml = manifest;
            if (manifest.startsWith('data:')) {
                const separatorIndex = manifest.indexOf(',');
                if (separatorIndex < 0) return;
                const metadata = manifest.slice(0, separatorIndex).toLowerCase();
                const payload = manifest.slice(separatorIndex + 1);
                xml = metadata.includes(';base64')
                    ? new TextDecoder().decode(Uint8Array.from(atob(payload), character => character.charCodeAt(0)))
                    : decodeURIComponent(payload);
            }
            const document = new DOMParser().parseFromString(xml, 'application/xml');
            document.querySelectorAll('Representation').forEach(representation => {
                const formatKey = this.getFormatKey(representation.querySelector('BaseURL')?.textContent ?? '');
                const index = this.parseRange(representation.querySelector('SegmentBase')?.getAttribute('indexRange'));
                if (!formatKey || !index) return;
                const contentLength = contentLengths.get(formatKey);
                const end = Math.min(
                    index.end + FIRST_MEDIA_PREFIX_BYTES,
                    Number.isFinite(contentLength) && contentLength! > 0 ? contentLength! - 1 : Number.MAX_SAFE_INTEGER
                );
                this.initialRangePlans.set(formatKey, { index, combined: { start: 0, end } });
            });
            this.initialRangePlans.forEach((plan, formatKey) => this.startInitialRangeSeed(formatKey, plan));
        } catch {
            // Unknown MPD shapes keep the existing per-range behavior.
        }
    }

    getPlayerTime(): number { return this.mediaElement.currentTime || 0; }
    getPlaybackRate(): number { return this.mediaElement.playbackRate || 1; }
    getBandwidthEstimate(): number { return this.player.getStats().estimatedBandwidth || 1_000_000; }
    getActiveTrackFormats(activeFormat: any): { audioFormat: any } { return { audioFormat: activeFormat }; }
    resetTiming(): void {
        this.timingContexts.clear();
        this.hedgeRequestsRemaining = 2;
        this.coalescedPrefixes.clear();
        this.seedUrls.clear();
        this.seedPromises.clear();
        this.coalescedRequests.clear();
        this.seedControllers.forEach(controller => controller.abort());
        this.seedControllers.clear();
        this.streamingRequests.clear();
        this.streamedResponses.clear();
        this.activeStreamingControllers.forEach(controller => controller.abort());
        this.activeStreamingControllers.clear();
        if (this.schemeRegistered) {
            this.schemeRuntime?.unregisterScheme(this.streamingScheme);
            this.schemeRegistered = false;
        }
        this.revokeLocalObjectUrls();
    }

    dispose(): void {
        this.requestInterceptor = undefined;
        this.responseInterceptor = undefined;
        this.metadataManager = undefined;
        this.cache = undefined;
        this.timingContexts.clear();
        this.initialRangePlans.clear();
        this.coalescedPrefixes.clear();
        this.seedUrls.clear();
        this.seedPromises.clear();
        this.coalescedRequests.clear();
        this.seedControllers.forEach(controller => controller.abort());
        this.seedControllers.clear();
        this.revokeLocalObjectUrls();
    }

    private async filterRequest(type: number, request: ShakaRequest, context?: ShakaContext): Promise<void> {
        if (type !== this.segmentRequestType || !this.requestInterceptor) return;
        const originalUrl = request.uris[0];
        if (!originalUrl?.startsWith('sabr:') && !this.isGoogleVideoUrl(originalUrl)) return;
        const originalHeaders = { ...request.headers };
        const logicalRange = this.getHeaderRange(originalHeaders);
        const formatKey = this.getFormatKey(originalUrl);
        if (formatKey && logicalRange) {
            const plan = this.initialRangePlans.get(formatKey);
            const seed = this.seedPromises.get(formatKey);
            if (plan && seed && logicalRange.start >= plan.combined.start &&
                logicalRange.end <= plan.combined.end) await seed;
            const localRange = this.getCoalescedRange(formatKey, logicalRange);
            if (localRange) {
                this.useLocalRange(request, localRange, originalHeaders, context, {
                    coalescingStatus: 'cache-hit',
                    networkRange: `${logicalRange.start}-${logicalRange.end}`
                });
                return;
            }
        }
        const cachedRange = !context?.segment
            ? this.getCachedInitRange(originalUrl, originalHeaders)
            : undefined;
        if (cachedRange) {
            // SABR returns the whole init container even for an index-range request.
            // Reuse it for Shaka's following init/index request instead of another CDN round-trip.
            this.useLocalRange(request, cachedRange, originalHeaders, context);
            return;
        }
        let coalesced: CoalescedRequest | undefined;
        let coalescingDiagnostics: CoalescingDiagnostics | undefined;
        if (formatKey && logicalRange) {
            const plan = this.initialRangePlans.get(formatKey);
            const prefix = this.coalescedPrefixes.get(formatKey);
            if (context?.segment && plan && prefix &&
                logicalRange.start < prefix.byteLength && logicalRange.end >= prefix.byteLength) {
                coalesced = {
                    formatKey, mode: 'tail', logicalRange,
                    networkRange: { start: prefix.byteLength, end: logicalRange.end },
                    originalUrl, originalHeaders
                };
            }
            if (coalesced) {
                coalescingDiagnostics = {
                    coalescingStatus: coalesced.mode,
                    networkRange: `${coalesced.networkRange.start}-${coalesced.networkRange.end}`
                };
            } else if (!context?.segment) {
                coalescingDiagnostics = {
                    coalescingStatus: !plan ? 'plan-missing'
                        : !this.rangesEqual(logicalRange, plan.index) ? 'range-mismatch'
                            : this.seedPromises.has(formatKey) ? 'busy' : 'plan-missing'
                };
            }
        } else if (!context?.segment) {
            coalescingDiagnostics = { coalescingStatus: 'plan-missing' };
        }
        const interceptorHeaders = { ...request.headers };
        if (coalesced) this.setRangeHeader(interceptorHeaders, coalesced.networkRange);
        const modified = await this.requestInterceptor({
            url: originalUrl,
            method: request.method || 'GET',
            headers: interceptorHeaders,
            body: request.body,
            segment: this.toSegment(context)
        });
        if (!modified) return;
        const proxyUrl = this.toProxyUrl(modified.url, this.takeHedgeSlot());
        request.method = modified.method;
        request.headers = { ...modified.headers, 'Content-Type': 'application/x-protobuf' };
        request.body = modified.body;
        if (coalesced) this.coalescedRequests.set(proxyUrl, coalesced);
        if (this.schemeRegistered) {
            const streamUrl = `${this.streamingScheme}:${encodeURIComponent(proxyUrl)}`;
            this.streamingRequests.set(streamUrl, {
                proxyUrl,
                context,
                timingHeaders: { ...originalHeaders, ...request.headers }
            });
            request.uris = [streamUrl];
        } else {
            request.uris = [proxyUrl];
        }
        this.reportTiming(
            'request', proxyUrl, { ...originalHeaders, ...request.headers }, context, undefined,
            coalescingDiagnostics
        );
    }

    private async filterResponse(type: number, response: ShakaResponse, context?: ShakaContext): Promise<void> {
        if (type !== this.segmentRequestType) return;
        const url = response.uri || response.originalRequest?.uris[0];
        if (url && this.localObjectUrls.delete(url)) {
            this.reportTiming('response', url, response.originalRequest?.headers, context, response.data.byteLength);
            this.reportTiming('processed', url, response.originalRequest?.headers, context, response.data.byteLength);
            this.reportTiming('delivered', url, response.originalRequest?.headers, context, response.data.byteLength);
            URL.revokeObjectURL(url);
            return;
        }
        if (!this.responseInterceptor || !url) return;
        const alreadyProcessed = this.streamedResponses.delete(url);
        if (!alreadyProcessed) {
            if (!this.metadataManager?.getRequestMetadata(url)) return;
            const raw = new Uint8Array(response.data);
            this.reportTiming('response', url, response.originalRequest?.headers, context, response.data.byteLength, {
                responseHash: formatHash(updateFnv1a(FNV_OFFSET, raw)),
                prefixHash: formatHash(updateFnv1a(FNV_OFFSET, raw.subarray(0, HASH_PREFIX_BYTES)))
            });
            response.data = await this.processUmp(url, response.data);
            this.reportTiming('processed', url, response.originalRequest?.headers, context, response.data.byteLength);
        }
        const updated = await this.responseInterceptor({
            url,
            method: response.originalRequest?.method || 'POST',
            headers: response.headers || {},
            data: response.data,
            makeRequest: (nextUrl: string, headers: Record<string, string>) =>
                this.makeRequest(nextUrl, headers, context)
        });
        if (updated?.data) response.data = this.toArrayBuffer(updated.data);
        response.data = await this.finishCoalescedRequest(url, response.data, context);
        this.reportTiming('delivered', url, response.originalRequest?.headers, context, response.data.byteLength);
    }

    private registerStreamingScheme(): void {
        if (!this.schemeRuntime || this.schemeRegistered) return;
        this.schemeRuntime.registerScheme(
            this.streamingScheme,
            (uri: string, request: ShakaRequest, _type: number, _progress: unknown,
                headersReceived: (headers: Record<string, string>) => void) =>
                this.createStreamingOperation(uri, request, headersReceived)
        );
        this.schemeRegistered = true;
    }

    private createStreamingOperation(
        uri: string,
        request: ShakaRequest,
        headersReceived: (headers: Record<string, string>) => void
    ): any {
        const controller = new AbortController();
        this.activeStreamingControllers.add(controller);
        const promise = this.fetchStreamingRequest(uri, request, controller, headersReceived)
            .finally(() => this.activeStreamingControllers.delete(controller));
        return this.schemeRuntime!.createAbortableOperation(promise, async () => {
            controller.abort();
        });
    }

    private async fetchStreamingRequest(
        uri: string,
        request: ShakaRequest,
        controller: AbortController,
        headersReceived: (headers: Record<string, string>) => void
    ): Promise<ShakaResponse & { originalUri: string; status: number; originalRequest: ShakaRequest }> {
        const pending = this.streamingRequests.get(uri);
        if (!pending) throw new Error(`Unknown SABR streaming request: ${uri}`);
        const startedAt = performance.now();
        const upstream = await fetch(pending.proxyUrl, {
            method: request.method,
            headers: request.headers,
            body: this.toBody(request.body),
            signal: controller.signal
        });
        const responseStartedAt = performance.now();
        const responseHeaders = Object.fromEntries(upstream.headers.entries());
        const diagnosticId = upstream.headers.get('x-sabr-diagnostic-id');
        headersReceived(responseHeaders);
        const { data, receivedBytes, responseHash, prefixHash, mediaEndBytes } = await this.readResponseData(
            pending.proxyUrl,
            upstream,
            () => controller.abort()
        );
        const responseEndedAt = performance.now();
        this.reportTiming('response', pending.proxyUrl, pending.timingHeaders, pending.context, receivedBytes, {
            responseHash,
            prefixHash,
            mediaEndBytes,
            resourceTiming: {
                name: pending.proxyUrl,
                entryType: 'resource',
                startTime: startedAt,
                duration: responseEndedAt - startedAt,
                responseStart: responseStartedAt,
                responseEnd: responseEndedAt,
                encodedBodySize: receivedBytes,
                serverTiming: this.parseServerTiming(upstream.headers.get('server-timing'))
            }
        });
        if (diagnosticId) {
            void this.loadProxyDiagnostic(diagnosticId).then(proxyDiagnostic => {
                this.reportTiming('server', pending.proxyUrl, pending.timingHeaders, pending.context, receivedBytes, {
                    proxyDiagnostic
                });
            }).catch(() => undefined);
        }
        this.reportTiming('processed', pending.proxyUrl, pending.timingHeaders, pending.context, data.byteLength);
        this.streamedResponses.add(pending.proxyUrl);
        this.streamingRequests.delete(uri);
        return {
            uri: pending.proxyUrl,
            originalUri: pending.proxyUrl,
            data,
            headers: responseHeaders,
            status: upstream.status,
            originalRequest: request
        };
    }

    private parseServerTiming(value: string | null): SabrResourceTiming['serverTiming'] {
        if (!value) return [];
        return value.split(',').flatMap(item => {
            const [namePart, ...parameters] = item.trim().split(';');
            if (!namePart) return [];
            const duration = Number(parameters
                .find(parameter => parameter.trim().toLowerCase().startsWith('dur='))
                ?.trim().slice(4));
            const description = parameters
                .find(parameter => parameter.trim().toLowerCase().startsWith('desc='))
                ?.trim().slice(5).replace(/^"|"$/g, '');
            return [{
                name: namePart,
                duration: Number.isFinite(duration) ? duration : 0,
                ...(description ? { description } : {})
            }];
        });
    }

    private async loadProxyDiagnostic(id: string): Promise<SabrProxyDiagnostic> {
        const response = await fetch(`/api/tracks/sabr-proxy-diagnostic?id=${encodeURIComponent(id)}`);
        if (!response.ok) throw new Error(`SABR diagnostic HTTP ${response.status}`);
        return response.json() as Promise<SabrProxyDiagnostic>;
    }

    private getCachedInitRange(url: string, headers: Record<string, string>): ArrayBuffer | undefined {
        const rangeHeader = Object.entries(headers)
            .find(([name]) => name.toLowerCase() === 'range')?.[1];
        const match = rangeHeader?.match(/^bytes=(\d+)-(\d+)$/i);
        if (!match || !this.cache?.getCacheEntries || !this.cache?.getInitSegment) return undefined;
        let formatKey: string | null = null;
        try { formatKey = new URL(url, window.location.origin).searchParams.get('key'); } catch { return undefined; }
        if (!formatKey) return undefined;
        const initEntries: Map<string, unknown> | undefined = this.cache.getCacheEntries()?.initSegmentCache;
        const cacheKey = initEntries && Array.from(initEntries.keys())
            .find(key => key.startsWith(`${formatKey}:`));
        if (!cacheKey) return undefined;
        const data = this.cache.getInitSegment(cacheKey) as ArrayBuffer | ArrayBufferView | undefined;
        if (!data) return undefined;
        const bytes = new Uint8Array(this.toArrayBuffer(data));
        const start = Number(match[1]);
        const end = Number(match[2]);
        if (start > end || end >= bytes.byteLength) return undefined;
        return bytes.slice(start, end + 1).buffer;
    }

    private getCoalescedRange(formatKey: string, range: ByteRange): ArrayBuffer | undefined {
        const prefix = this.coalescedPrefixes.get(formatKey);
        if (!prefix || range.end >= prefix.byteLength) return undefined;
        return prefix.slice(range.start, range.end + 1);
    }

    private startInitialRangeSeed(formatKey: string, plan: InitialRangePlan): void {
        const url = this.seedUrls.get(formatKey);
        if (!url || this.seedPromises.has(formatKey)) return;
        const controller = new AbortController();
        this.seedControllers.add(controller);
        const range = `${plan.combined.start}-${plan.combined.end}`;
        const expectedBytes = plan.combined.end - plan.combined.start + 1;
        const seed = fetch(url, {
            headers: { Range: `bytes=${range}` },
            signal: controller.signal
        }).then(async response => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.arrayBuffer();
            if (data.byteLength !== expectedBytes) {
                throw new Error(`Expected ${expectedBytes} bytes, received ${data.byteLength}`);
            }
            if (controller.signal.aborted) return false;
            this.coalescedPrefixes.set(formatKey, data);
            return true;
        }).catch(() => false).finally(() => this.seedControllers.delete(controller));
        this.seedPromises.set(formatKey, seed);
    }

    private useLocalRange(
        request: ShakaRequest,
        data: ArrayBuffer,
        originalHeaders: Record<string, string>,
        context?: ShakaContext,
        diagnostics?: CoalescingDiagnostics
    ): void {
        const url = URL.createObjectURL(new Blob([data]));
        this.localObjectUrls.add(url);
        request.uris = [url];
        request.body = null;
        Object.keys(request.headers)
            .filter(name => name.toLowerCase() === 'range')
            .forEach(name => delete request.headers[name]);
        this.reportTiming('request', url, originalHeaders, context, undefined, diagnostics);
    }

    private async finishCoalescedRequest(
        url: string,
        data: ArrayBuffer,
        context?: ShakaContext
    ): Promise<ArrayBuffer> {
        const pending = this.coalescedRequests.get(url);
        if (!pending) return data;
        this.coalescedRequests.delete(url);

        const expectedNetworkLength = pending.networkRange.end - pending.networkRange.start + 1;
        if (data.byteLength !== expectedNetworkLength) {
            this.updateCoalescingDiagnostics(url, {
                coalescingStatus: 'fallback-size',
                expectedBytes: expectedNetworkLength,
                actualBytes: data.byteLength
            });
            return this.repeatOriginalRequest(pending, context);
        }

        const prefix = this.coalescedPrefixes.get(pending.formatKey);
        if (!prefix || pending.logicalRange.start >= prefix.byteLength) {
            this.updateCoalescingDiagnostics(url, { coalescingStatus: 'fallback-prefix' });
            return this.repeatOriginalRequest(pending, context);
        }
        const prefixPart = prefix.slice(
            pending.logicalRange.start,
            Math.min(pending.logicalRange.end + 1, prefix.byteLength)
        );
        const joined = new Uint8Array(prefixPart.byteLength + data.byteLength);
        joined.set(new Uint8Array(prefixPart));
        joined.set(new Uint8Array(data), prefixPart.byteLength);
        if (joined.byteLength !== pending.logicalRange.end - pending.logicalRange.start + 1) {
            this.updateCoalescingDiagnostics(url, {
                coalescingStatus: 'fallback-joined-size',
                expectedBytes: pending.logicalRange.end - pending.logicalRange.start + 1,
                actualBytes: joined.byteLength
            });
            return this.repeatOriginalRequest(pending, context);
        }
        this.updateCoalescingDiagnostics(url, {
            coalescingStatus: 'tail-joined',
            expectedBytes: pending.logicalRange.end - pending.logicalRange.start + 1,
            actualBytes: joined.byteLength
        });
        return joined.buffer;
    }

    private updateCoalescingDiagnostics(url: string, diagnostics: CoalescingDiagnostics): void {
        const timing = this.timingContexts.get(url);
        if (timing) Object.assign(timing, diagnostics);
    }

    private async repeatOriginalRequest(
        pending: CoalescedRequest,
        context?: ShakaContext
    ): Promise<ArrayBuffer> {
        const response = await this.makeRequest(pending.originalUrl, pending.originalHeaders, context);
        return response?.data ? this.toArrayBuffer(response.data) : new ArrayBuffer(0);
    }

    private getHeaderRange(headers: Record<string, string>): ByteRange | undefined {
        const value = Object.entries(headers).find(([name]) => name.toLowerCase() === 'range')?.[1];
        return value ? this.parseRange(value) : undefined;
    }

    private parseRange(value: string | null | undefined): ByteRange | undefined {
        const match = value?.trim().match(/^(?:bytes=)?(\d+)-(\d+)$/i);
        if (!match) return undefined;
        const start = Number(match[1]);
        const end = Number(match[2]);
        return Number.isSafeInteger(start) && Number.isSafeInteger(end) && start <= end
            ? { start, end }
            : undefined;
    }

    private setRangeHeader(headers: Record<string, string>, range: ByteRange): void {
        const name = Object.keys(headers).find(header => header.toLowerCase() === 'range') ?? 'Range';
        headers[name] = `bytes=${range.start}-${range.end}`;
    }

    private rangesEqual(left: ByteRange, right: ByteRange): boolean {
        return left.start === right.start && left.end === right.end;
    }

    private getFormatKey(url: string): string | undefined {
        try {
            return new URL(url, window.location.origin).searchParams.get('key')?.split(':')[0] || undefined;
        } catch {
            return undefined;
        }
    }

    private revokeLocalObjectUrls(): void {
        this.localObjectUrls.forEach(url => URL.revokeObjectURL(url));
        this.localObjectUrls.clear();
    }

    private async makeRequest(url: string, headers: Record<string, string>, context?: ShakaContext): Promise<any> {
        const request = {
            url,
            method: 'GET',
            headers: { ...headers },
            segment: this.toSegment(context)
        };
        const modified = this.requestInterceptor ? await this.requestInterceptor(request) : request;
        const requestUrl = this.toProxyUrl(modified.url, this.takeHedgeSlot());
        const timingHeaders = { ...headers, ...modified.headers };
        this.reportTiming('request', requestUrl, timingHeaders, context);
        const requestController = new AbortController();
        const upstream = await fetch(requestUrl, {
            method: modified.method,
            headers: { ...modified.headers, 'Content-Type': 'application/x-protobuf' },
            body: this.toBody(modified.body),
            signal: requestController.signal
        });
        const { data, receivedBytes, responseHash, prefixHash, mediaEndBytes } = await this.readResponseData(
            requestUrl,
            upstream,
            () => requestController.abort()
        );
        this.reportTiming('response', requestUrl, timingHeaders, context, receivedBytes, {
            responseHash,
            prefixHash,
            mediaEndBytes
        });
        this.reportTiming('processed', requestUrl, timingHeaders, context, data.byteLength);
        const result = {
            url: requestUrl,
            method: modified.method,
            headers: Object.fromEntries(upstream.headers.entries()),
            data,
            makeRequest: (nextUrl: string, nextHeaders: Record<string, string>) =>
                this.makeRequest(nextUrl, nextHeaders, context)
        };
        const updated = this.responseInterceptor ? await this.responseInterceptor(result) : result;
        const delivered = updated?.data ? this.toArrayBuffer(updated.data) : data;
        this.reportTiming('delivered', requestUrl, timingHeaders, context, delivered.byteLength);
        return updated ?? result;
    }

    private async readResponseData(
        url: string,
        response: Response,
        abortRequest?: () => void
    ): Promise<{
        data: ArrayBuffer;
        receivedBytes: number;
        responseHash: string;
        prefixHash: string;
        mediaEndBytes?: number;
    }> {
        const metadata = this.metadataManager?.getRequestMetadata(url);
        if (!metadata?.isUMP || !response.body) {
            const raw = await response.arrayBuffer();
            const bytes = new Uint8Array(raw);
            return {
                data: await this.processUmp(url, raw),
                receivedBytes: raw.byteLength,
                responseHash: formatHash(updateFnv1a(FNV_OFFSET, bytes)),
                prefixHash: formatHash(updateFnv1a(FNV_OFFSET, bytes.subarray(0, HASH_PREFIX_BYTES)))
            };
        }

        const { SabrUmpProcessor } = loadSabrRuntime();
        const processor = new SabrUmpProcessor(metadata, this.cache);
        const reader = response.body.getReader();
        let receivedBytes = 0;
        let responseHash = FNV_OFFSET;
        let prefixHash = FNV_OFFSET;
        let prefixBytes = 0;

        while (true) {
            const chunk = await reader.read();
            if (chunk.done) {
                return {
                    data: new ArrayBuffer(0),
                    receivedBytes,
                    responseHash: formatHash(responseHash),
                    prefixHash: formatHash(prefixHash)
                };
            }
            receivedBytes += chunk.value.byteLength;
            responseHash = updateFnv1a(responseHash, chunk.value);
            if (prefixBytes < HASH_PREFIX_BYTES) {
                const prefixChunk = chunk.value.subarray(0, HASH_PREFIX_BYTES - prefixBytes);
                prefixHash = updateFnv1a(prefixHash, prefixChunk);
                prefixBytes += prefixChunk.byteLength;
            }
            const result = await processor.processChunk(chunk.value);
            if (!result?.done) continue;

            // MediaEnd is a complete segment boundary. Do not wait for the SABR
            // HTTP response to close; the remaining bytes are not needed by Shaka.
            abortRequest?.();
            void reader.cancel().catch(() => undefined);
            return {
                data: result.data ? this.toArrayBuffer(result.data) : new ArrayBuffer(0),
                receivedBytes,
                responseHash: formatHash(responseHash),
                prefixHash: formatHash(prefixHash),
                mediaEndBytes: receivedBytes
            };
        }
    }

    private async processUmp(url: string, data: ArrayBuffer): Promise<ArrayBuffer> {
        const metadata = this.metadataManager?.getRequestMetadata(url);
        if (!metadata?.isUMP) return data;
        const { SabrUmpProcessor } = loadSabrRuntime();
        const result = await new SabrUmpProcessor(metadata, this.cache).processChunk(new Uint8Array(data));
        return result?.data ? this.toArrayBuffer(result.data) : new ArrayBuffer(0);
    }

    private reportTiming(
        phase: SabrTimingEvent['phase'],
        url: string,
        headers: Record<string, string> | undefined,
        context?: ShakaContext,
        bytes?: number,
        diagnostics?: Pick<SabrTimingEvent,
            'responseHash' | 'prefixHash' | 'mediaEndBytes' | 'resourceTiming' | 'proxyDiagnostic'> & CoalescingDiagnostics
    ): void {
        const knownContext = this.timingContexts.get(url);
        const range = Object.entries(headers ?? {})
            .find(([name]) => name.toLowerCase() === 'range')?.[1]
            ?.replace(/^bytes=/i, '') ?? knownContext?.range;
        const hasSegment = Boolean(context?.segment) || Boolean(knownContext?.hasSegment);
        const timing = { ...knownContext, range, hasSegment, ...diagnostics };
        if (phase === 'request') this.timingContexts.set(url, timing);
        this.onTiming?.({ phase, url, bytes, ...timing });
    }

    private toSegment(context?: ShakaContext) {
        return {
            getStartTime: () => context?.segment?.getStartTime?.() ?? context?.segment?.startTime ?? null,
            // Shaka omits segment for both initialization and index-range requests.
            // The SABR processor then extracts only the requested MP4 byte range.
            isInit: () => !context?.segment
        };
    }

    private takeHedgeSlot(): boolean {
        if (this.hedgeRequestsRemaining <= 0) return false;
        this.hedgeRequestsRemaining -= 1;
        return true;
    }

    private toProxyUrl(url: string, hedge = false): string {
        const parsed = new URL(url, window.location.origin);
        if (parsed.origin === window.location.origin && parsed.pathname === '/api/tracks/sabr-proxy') {
            if (hedge) parsed.searchParams.set('hedge', '1');
            return parsed.toString();
        }
        if (!this.isGoogleVideoUrl(parsed.toString())) return parsed.toString();
        const proxy = new URL('/api/tracks/sabr-proxy', window.location.origin);
        proxy.searchParams.set('url', parsed.toString());
        const requestNumber = parsed.searchParams.get('rn');
        if (requestNumber) proxy.searchParams.set('rn', requestNumber);
        if (hedge) proxy.searchParams.set('hedge', '1');
        return proxy.toString();
    }

    private isGoogleVideoUrl(url: string): boolean {
        try {
            const hostname = new URL(url, window.location.origin).hostname;
            return hostname === 'googlevideo.com' || hostname.endsWith('.googlevideo.com');
        } catch { return false; }
    }

    private toBody(body?: ArrayBuffer | ArrayBufferView | null): BodyInit | undefined {
        if (!body) return undefined;
        return this.toArrayBuffer(body);
    }

    private toArrayBuffer(data: ArrayBuffer | ArrayBufferView): ArrayBuffer {
        if (data instanceof ArrayBuffer) return data;
        return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
    }
}

export function createSabrAdapter(
    bridge: ShakaSabrPlayerAdapter,
    config: ITrackPlaybackSource['sabr'],
    player: unknown
): any {
    const { SabrStreamingAdapter } = loadSabrRuntime();
    const { buildSabrFormat } = loadUtilsRuntime();
    const adapter = new SabrStreamingAdapter({
        playerAdapter: bridge,
        clientInfo: config.clientInfo
    });
    adapter.onMintPoToken(async () => config.poToken);
    adapter.attach(player);
    adapter.setStreamingURL(config.streamingUrl);
    adapter.setUstreamerConfig(config.ustreamerConfig);
    adapter.setServerAbrFormats(config.formats.map(buildSabrFormat));
    return adapter;
}
